"use client";

import { Button, Card, Separator } from "@heroui/react";
import { motion, useReducedMotion } from "framer-motion";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

type CoinSide = "heads" | "tails";
type TossResult = { side: CoinSide; timestamp: Date };

const MIN_SPINS = 4;
const MAX_SPINS = 6;
const FLIP_DURATION = 1.5;
const REDUCED_MOTION_DURATION = 0.5;

const TossCoinGame: React.FC = () => {
	const [isFlipping, setIsFlipping] = useState(false);
	const [currentSide, setCurrentSide] = useState<CoinSide>("heads");
	const [targetRotation, setTargetRotation] = useState(0);
	const [isUserFlip, setIsUserFlip] = useState(false);

	const [tossHistory, setTossHistory] = useState<TossResult[]>([]);
	const [headsCount, setHeadsCount] = useState(0);
	const [tailsCount, setTailsCount] = useState(0);
	const [flipSound, setFlipSound] = useState<HTMLAudioElement | null>(null);

	const shouldReduceMotion = useReducedMotion();

	useEffect(() => {
		setFlipSound(new Audio("/im/coin/flip.wav"));
	}, []);

	const tossCoin = useCallback(() => {
		if (isFlipping) return;

		setIsUserFlip(true);
		setIsFlipping(true);

		if (flipSound) {
			flipSound.currentTime = 0;
			flipSound.play().catch((err) => console.error("Error playing sound:", err));
		}

		const result: CoinSide = Math.random() < 0.5 ? "heads" : "tails";

		setCurrentSide(result);

		setTargetRotation((prevRotation) => {
			let newRotation;

			if (shouldReduceMotion) {
				const currentVisualIsHeads = Math.round(prevRotation / 180) % 2 === 0;
				const needsToFlipToHeads = result === "heads" && !currentVisualIsHeads;
				const needsToFlipToTails = result === "tails" && currentVisualIsHeads;

				if (needsToFlipToHeads || needsToFlipToTails) {
					newRotation = prevRotation + 180;
				} else {
					newRotation = prevRotation + 360;
				}
			} else {
				const randomSpins =
					MIN_SPINS + Math.floor(Math.random() * (MAX_SPINS - MIN_SPINS + 1));

				newRotation = prevRotation + randomSpins * 360;

				const wouldLandHeadsAfterSpins =
					Math.round(newRotation / 180) % 2 === 0;

				if (result === "tails" && wouldLandHeadsAfterSpins) {
					newRotation += 180;
				} else if (result === "heads" && !wouldLandHeadsAfterSpins) {
					newRotation += 180;
				}
			}

			return newRotation;
		});
	}, [isFlipping, flipSound, shouldReduceMotion]);

	// Keyboard support: spacebar or enter to flip
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === " " || e.key === "Enter") {
				if (
					e.target instanceof HTMLInputElement ||
					e.target instanceof HTMLTextAreaElement ||
					e.target instanceof HTMLButtonElement
				) {
					return;
				}
				e.preventDefault();
				tossCoin();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [tossCoin]);

	const handleAnimationComplete = () => {
		if (isUserFlip) {
			setIsFlipping(false);

			if (currentSide === "heads") {
				setHeadsCount((prev) => prev + 1);
			} else {
				setTailsCount((prev) => prev + 1);
			}

			setTossHistory((prev) => [
				{ side: currentSide, timestamp: new Date() },
				...prev.slice(0, 9),
			]);
			setIsUserFlip(false);
		} else {
			if (isFlipping) setIsFlipping(false);
		}
	};

	const getAnimationDuration = () => {
		return shouldReduceMotion ? REDUCED_MOTION_DURATION : FLIP_DURATION;
	};

	const totalFlips = headsCount + tailsCount;
	const headsPercent =
		totalFlips > 0 ? Math.round((headsCount / totalFlips) * 100) : 0;
	const tailsPercent = totalFlips > 0 ? 100 - headsPercent : 0;

	return (
		<Card className="glass-pro w-full shadow-large">
			<Card.Header className="flex flex-col items-center gap-1 py-4 sm:py-6 px-4 sm:px-6">
				<h2
					aria-live="polite"
					className="text-xl sm:text-2xl font-bold text-center text-foreground"
				>
					{isFlipping
						? "Flipping..."
						: `Landed on ${currentSide.charAt(0).toUpperCase() + currentSide.slice(1)}`}
				</h2>
				<p className="text-default-500 text-sm text-center">
					Press space or tap the button to flip
				</p>
			</Card.Header>

			<Separator className="opacity-30" />

			<Card.Content className="flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
				<div
					className="relative mb-8 mt-4 flex justify-center w-full"
					style={{ perspective: "1000px" }}
				>
					{/* Coin backdrop glow */}
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="w-36 h-36 sm:w-48 sm:h-48 md:w-56 md:h-56 rounded-full bg-dogeYellow-500/5 dark:bg-dogeYellow-500/10 blur-xl" />
					</div>

					<motion.div
						animate={{
							rotateX: targetRotation,
						}}
						className="w-40 h-40 sm:w-56 sm:h-56 md:w-64 md:h-64 relative"
						initial={{ rotateX: 0 }}
						style={{
							transformStyle: "preserve-3d",
						}}
						transition={{
							duration: getAnimationDuration(),
							ease: "easeInOut",
						}}
						onAnimationComplete={handleAnimationComplete}
					>
						<div
							className="absolute w-full h-full rounded-full flex items-center justify-center"
							style={{
								backfaceVisibility: "hidden",
								transform: "rotateX(0deg)",
							}}
						>
							<img
								alt="Dogecoin Heads"
								className="w-full h-full rounded-full object-cover drop-shadow-lg"
								src="/im/coin/head.svg"
							/>
						</div>
						<div
							className="absolute w-full h-full rounded-full flex items-center justify-center"
							style={{
								backfaceVisibility: "hidden",
								transform: "rotateX(180deg)",
							}}
						>
							<img
								alt="Dogecoin Tails"
								className="w-full h-full rounded-full object-cover drop-shadow-lg"
								src="/im/coin/tail.svg"
							/>
						</div>
					</motion.div>
				</div>

				<motion.div
					animate={isFlipping ? {} : { scale: [1, 1.05, 1] }}
					transition={{ duration: 0.3 }}
				>
					<Button
						aria-label={isFlipping ? "Flipping coin" : "Flip Coin"}
						className="font-semibold text-base px-8 bg-dogeYellow-500 text-eclipse hover:bg-dogeYellow-600"
						isDisabled={isFlipping}
						size="lg"
						variant="primary"
						onPress={tossCoin}
					>
						{isFlipping ? (
							<svg
								className="animate-spin"
								fill="none"
								height="20"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								viewBox="0 0 24 24"
								width="20"
							>
								<path d="M21 12a9 9 0 1 1-6.219-8.56" />
							</svg>
						) : (
							<svg
								fill="none"
								height="20"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								viewBox="0 0 24 24"
								width="20"
							>
								<path d="M21 2v6h-6" />
								<path d="M21 13a9 9 0 1 1-3-7.7L21 8" />
							</svg>
						)}
						{isFlipping ? "Flipping..." : "Flip Coin"}
					</Button>
				</motion.div>
			</Card.Content>

			<Separator className="opacity-30" />

			<Card.Footer className="flex flex-col p-4 sm:p-6">
				{/* Stats row */}
				<div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 w-full">
					<div className="text-center p-3 rounded-xl bg-dogeYellow-500/5 dark:bg-dogeYellow-500/10">
						<p className="text-xs uppercase tracking-wider text-default-500 mb-1">
							Heads
						</p>
						<p className="text-2xl sm:text-3xl font-bold text-foreground">
							{headsCount}
						</p>
						<p className="text-xs text-default-500 mt-1">
							{headsPercent}%
						</p>
					</div>
					<div className="text-center p-3 rounded-xl bg-default/30">
						<p className="text-xs uppercase tracking-wider text-default-500 mb-1">
							Total
						</p>
						<p className="text-2xl sm:text-3xl font-bold text-foreground">
							{totalFlips}
						</p>
						<p className="text-xs text-default-500 mt-1">
							{tossHistory.length > 0
								? tossHistory[0].timestamp.toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit",
									})
								: "—"}
						</p>
					</div>
					<div className="text-center p-3 rounded-xl bg-dogeYellow-500/5 dark:bg-dogeYellow-500/10">
						<p className="text-xs uppercase tracking-wider text-default-500 mb-1">
							Tails
						</p>
						<p className="text-2xl sm:text-3xl font-bold text-foreground">
							{tailsCount}
						</p>
						<p className="text-xs text-default-500 mt-1">
							{tailsPercent}%
						</p>
					</div>
				</div>

				{/* Heads/Tails ratio bar */}
				{totalFlips > 0 && (
					<div className="w-full h-2 rounded-full overflow-hidden bg-default/30 mb-6 flex">
						<motion.div
							animate={{ width: `${headsPercent}%` }}
							className="h-full bg-dogeYellow-500"
							initial={{ width: 0 }}
							transition={{ duration: 0.5, ease: "easeOut" }}
						/>
						<motion.div
							animate={{ width: `${tailsPercent}%` }}
							className="h-full bg-dogeYellow-700"
							initial={{ width: 0 }}
							transition={{ duration: 0.5, ease: "easeOut" }}
						/>
					</div>
				)}

				<Separator className="my-2 opacity-30" />

				{/* History section */}
				<div className="flex justify-between items-center mb-3 mt-2">
					<h4 className="text-sm font-medium text-default-500 uppercase tracking-wider">
						Flip History
					</h4>
					{tossHistory.length > 0 && (
						<Button
							isIconOnly
							aria-label="Clear History"
							className="text-default-500"
							size="sm"
							variant="ghost"
							onPress={() => {
								setTossHistory([]);
								setHeadsCount(0);
								setTailsCount(0);
								setCurrentSide("heads");
								setTargetRotation(0);
							}}
						>
							<svg
								fill="none"
								height="16"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								viewBox="0 0 24 24"
								width="16"
							>
								<path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2M10 11v6M14 11v6" />
							</svg>
						</Button>
					)}
				</div>

				{tossHistory.length === 0 ? (
					<div className="text-default-500 text-center py-8 rounded-xl p-4 border border-dashed border-default/30">
						<p className="text-sm">No flips yet</p>
						<p className="text-xs text-default-500 mt-1">
							Tap the button or press space to start
						</p>
					</div>
				) : (
					<div className="flex flex-wrap justify-center gap-2">
						{tossHistory.map((toss, index) => (
							<motion.div
								key={`${toss.timestamp.getTime()}-${index}`}
								animate={{ opacity: 1, scale: 1 }}
								className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs font-bold ${
									toss.side === "heads"
										? "bg-dogeYellow-500/20 text-dogeYellow-600 dark:text-dogeYellow-400"
										: "bg-dogeYellow-700/20 text-dogeYellow-700 dark:text-dogeYellow-300"
								}`}
								initial={{ opacity: 0, scale: 0.5 }}
								title={`Flip ${tossHistory.length - index}: ${toss.side} at ${toss.timestamp.toLocaleTimeString()}`}
								transition={{ duration: 0.3, delay: index * 0.05 }}
							>
								{toss.side === "heads" ? "H" : "T"}
							</motion.div>
						))}
					</div>
				)}
			</Card.Footer>
		</Card>
	);
};

export default TossCoinGame;
